<script setup lang="ts">
import {usePage} from "@inertiajs/vue3";
import Layout from "@/Layouts/Frontend/Layout.vue";
import {
    FeatureValueResource,
    MediaCollectionTypeEnum,
    MediaResource,
    OptionTypeEnum,
    OptionValueResource,
    ProductResource,
    ProductVariantTypeEnum,
    SubscriptionCollectionResource,
    SubscriptionPlanResource,
    SubscriptionPriceRuleEnum,
    VariantResource,
} from "@/Types/generated.d";
import {computed, onMounted, Ref, ref} from "vue";
import {PageProps} from "@/Types";
import Slider from "@/Components/Frontend/Elements/Slider.vue";
import Button from "@/Components/Frontend/Elements/Button.vue";
import Spoiler from "@/Components/Frontend/Elements/Spoiler.vue";
import MainImage from "@/Pages/Frontend/Media/Partials/MainImage.vue";
import {useUtils} from "@/Composables/Shared/useUtils";
import {useCartStore} from "@/Stores/CartStore";
import Checklist from "@/Components/Frontend/Elements/Checklist.vue";
import {useWindowSize} from "@vueuse/core";
import PageTitle from "@/Components/Frontend/Elements/PageTitle.vue";
import SubscriptionIcon from "@/Components/Frontend/Icons/SubscriptionIcon.vue";
import {trans} from "laravel-vue-i18n";
import {useSanitize} from "@/Composables/Shared/useSanitize";
import ProductSlider from "@/Pages/Frontend/Product/Partials/ProductSlider.vue";
import Label from "@/Components/Frontend/Form/Label.vue";
import Textarea from "@/Components/Frontend/Form/Textarea.vue";
// --- Props
const props = defineProps<{
    product: ProductResource;
    relatedProducts: ProductResource[];
    subscriptionCollection?: SubscriptionCollectionResource;
}>();
// --- Data
const sanitize = useSanitize();
const cartStore = useCartStore();
const customizationText: Ref<string | null> = ref(null);
const currentVariant: Ref<VariantResource | undefined> = ref(undefined);
const currentSubscriptionPlan: Ref<SubscriptionPlanResource | undefined> = ref(undefined);
const { width } = useWindowSize();
// --- Computed
const initialPageProps = computed(() => usePage<PageProps>().props);
const priceInclTax = computed<number>(() => {
    if (
        currentSubscriptionPlan.value?.pivot_subscription_plan_product_issue?.price_rule ===
        SubscriptionPriceRuleEnum.SUBSCRIPTION
    ) {
        return currentSubscriptionPlan.value?.price_incl_tax ?? 0;
    }

    return currentVariant.value?.price_incl_tax ?? 0;
});
const featuresToList = computed<string>(() => {
    return props.product?.feature_values?.reduce((acc, featureValue: FeatureValueResource) => {
        return acc + `<li><strong>${featureValue.feature?.name}</strong> : ${featureValue.name}</li>`;
    }, "") ?? "";
});
const colors = computed<VariantResource[]>(() => {
    let variants = props.product.variants ?? [];

    return variants.filter((variant: VariantResource) => {
        return variant.option_values?.find((optionValue: OptionValueResource) => {
            return optionValue.option?.type === OptionTypeEnum.COLOR;
        }) ?? [];
    });
});
const images = computed<MediaResource[]>(() => {
    let medias = currentVariant.value?.medias?.length
        ? currentVariant.value?.medias ?? []
        : props.product.medias ?? [];

    return medias.filter((media: MediaResource) => {
        return media.collection_name === MediaCollectionTypeEnum.IMAGES && media.medium_url !== null;
    });
});
const subscriptionPlans = computed<SubscriptionPlanResource[]>(() => {
    return props.product.subscription_plans_as_issue?.filter((subscriptionPlan: SubscriptionPlanResource) => {
        return subscriptionPlan.pivot_subscription_plan_product_issue?.is_subscribable;
    }) ?? [];
});
const stockInfoText = computed<string | null>(() => {
    if (!currentVariant.value?.is_stock_enabled) {
        return null;
    }

    if (currentVariant.value?.quantity > 0) {
        return currentVariant.value?.in_stock_infos?.length
            ? currentVariant.value?.in_stock_infos
            : trans("In stock, ready to ship")
    }

    return currentVariant.value?.no_stock_infos?.length
        ? currentVariant.value?.no_stock_infos
        : trans("Out of stock");
});
const preorderInfoText = computed<string | null>(() => {
    if (!currentVariant.value?.is_preorder) {
        return null;
    }

    return currentVariant.value?.preorder_infos?.length
        ? currentVariant.value?.preorder_infos
        : trans("Available for pre-order");
});
const allowSubscription = computed<boolean>(() => {
    if (!props.subscriptionCollection) {
        return false; // If no subscription collection, the product is not subscribable
    }

    return props.product.subscription_plans_as_issue?.find((subscriptionPlan: SubscriptionPlanResource) => {
        return subscriptionPlan.pivot_subscription_plan_product_issue?.is_subscribable;
    }) !== undefined;
});
const allowPurchase = computed<boolean>(() => {
    if (!props.subscriptionCollection) {
        return true; // If no subscription collection, the product is considered purchasable
    }

    return props.product.subscription_plans_as_issue?.find((subscriptionPlan: SubscriptionPlanResource) => {
        return subscriptionPlan.pivot_subscription_plan_product_issue?.is_purchasable;
    }) !== undefined;
});
// --- Methods
const getVariantColor = (variant: VariantResource): string | null => {
    let optionValue = variant.option_values?.find((optionValue: OptionValueResource) => {
        return optionValue.option?.type === OptionTypeEnum.COLOR && optionValue.color !== null
    });

    return optionValue?.color ?? null
};
const onVariantChange = (variantId: number) => {
    currentVariant.value = props.product.variants?.find((variant: VariantResource) => {
        return variant.id === variantId;
    });
};
const onSubscriptionPlanChange = (subscriptionPlanId: number) => {
    currentSubscriptionPlan.value = subscriptionPlans.value?.find((subscriptionPlan: SubscriptionPlanResource) => {
        return subscriptionPlan.id === subscriptionPlanId
    });

    if (currentSubscriptionPlan.value?.subscription_type) {
        history.pushState(
            {},
            "",
            "#subscription-plan=" + currentSubscriptionPlan.value?.subscription_type?.slug
        );
    }
};
const initDefaultSubscriptionPlan = () => {
    const parsedHash = new URLSearchParams(window.location.hash.substring(1));

    if (parsedHash.get("subscription-plan")) {
        currentSubscriptionPlan.value = subscriptionPlans.value?.find((subscriptionPlan: SubscriptionPlanResource) => {
            return subscriptionPlan.subscription_type?.slug === parsedHash.get("subscription-plan");
        });
    }

    if (!currentSubscriptionPlan.value) {
        currentSubscriptionPlan.value = subscriptionPlans.value?.[0] ?? undefined;
    }
};
const initDefaultVariant = () => {
    const parsedHash = new URLSearchParams(window.location.hash.substring(1));

    if (parsedHash.get("variant")) {
        currentVariant.value = props.product?.variants?.find((variant: VariantResource) => {
            return variant.sku === parsedHash.get("variant");
        });
    }

    if (!currentVariant.value) {
        currentVariant.value = props.product?.variants?.find((variant: VariantResource) => variant.is_default);
    }
};
const handleAddToCart = (isSubscription: boolean) => {
    if (!currentVariant.value
        || !currentVariant.value.is_purchasable
        || (!customizationText.value && props.product.has_customization)
        || (isSubscription && !currentSubscriptionPlan.value)
    ) {
        return;
    }

    cartStore.addProduct(
        props.product,
        currentVariant.value,
        isSubscription ? currentSubscriptionPlan.value : null,
        !isSubscription ? customizationText.value : null,
    );
};
onMounted(() => {
    initDefaultSubscriptionPlan();
    initDefaultVariant();
});
// --- Watch
// --- Provide
</script>

<template>
    <Layout :seo="product.seo" show-breadcrumb :canonical="route('frontend.product', product.slug)">
        <PageTitle :title="product.name" />

        <div class="single-product">
            <div class="single-product__overview">
                <Slider v-if="images.length" :slides="images" :loop="true" class="single-product__slider">
                    <template #slideContent="{ item, index }">
                        <MainImage
                            class="w-full h-full object-cover object-center"
                            :media="item"
                            :alt="item.name"
                            :loading="index === 0 ? null : 'lazy'"
                            format="large"
                        />
                    </template>
                </Slider>
                <MainImage
                    v-else
                    class="single-product__slider"
                    :alt="product.name"
                    format="large"
                />
                <template v-if="width >= 1024">
                    <div class="mb-10 text-center lg:text-left">
                        <span class="block font-sans text-fsm font-bold"> {{ $t("Product overview") }} </span>
                        <div class="prose max-w-none text-fsm" v-html="sanitize.html(product.description)"></div>
                    </div>
                    <Spoiler
                        v-if="featuresToList !== ''"
                        :title="$t('Technical data')"
                        class="!px-5 !py-3 text-left"
                        icon-class="w-4 h-4"
                    >
                        <ul v-html="sanitize.html(featuresToList)"></ul>
                    </Spoiler>
                </template>
            </div>

            <div class="single-product__details">
                <div class="mb-3 font-sans font-bold">{{ $t("Reference") }} : {{ currentVariant?.sku }}</div>
                <div v-if="product.summary" class="mb-6 font-sans text-fsm font-light">
                    {{ product.summary }}
                </div>
                <div class="mb-8 items-end justify-center lg:flex lg:justify-start">
                    <div class="mr-3 font-roboto text-f2xl font-black lg:leading-none">
                        {{ useUtils().toCurrency(priceInclTax, initialPageProps.default_currency) }}
                    </div>
                    <div class="font-sans font-light">
                        <template
                            v-if="
                                currentSubscriptionPlan?.pivot_subscription_plan_product_issue?.price_rule ===
                                SubscriptionPriceRuleEnum.PRODUCT
                            "
                        >
                            {{ $t("then") }}
                            <strong>{{
                                useUtils().toCurrency(
                                    currentSubscriptionPlan?.price_incl_tax,
                                    initialPageProps.default_currency,
                                )
                            }}</strong>
                            {{ $t("per delivery") }}
                        </template>
                        <template v-if="initialPageProps.default_country === 'AU'"> ({{ $t("incl. P&P") }}) </template>
                        <template v-if="currentSubscriptionPlan">
                            / {{ currentSubscriptionPlan?.subscription_type?.frequency_label?.toLowerCase() }}
                        </template>
                    </div>
                </div>
                <div
                    v-if="currentVariant?.is_stock_enabled || currentVariant?.is_preorder"
                    class="mb-10 flex items-center justify-center lg:justify-start"
                >
                    <span
                        class="mr-4 block h-3 w-3 rounded-full"
                        :class="{
                            'bg-blue-400': currentVariant?.is_preorder,
                            'bg-red-400': currentVariant?.is_stock_enabled && currentVariant?.quantity <= 0,
                            'bg-green-400': currentVariant?.is_stock_enabled && currentVariant?.quantity > 0,
                        }"
                    >
                    </span>
                    <span v-if="currentVariant?.is_stock_enabled" class="font-sans text-fsm font-light">
                        {{ stockInfoText }}
                    </span>
                    <span v-if="currentVariant?.is_preorder" class="font-sans text-fsm font-light">
                        {{ preorderInfoText }}
                    </span>
                </div>
                <div v-if="product.variant_type === ProductVariantTypeEnum.WITH_OPTIONS && colors.length" class="mb-7">
                    <span class="mb-2 block">{{ $t("Color") }} :</span>
                    <div class="flex items-center space-x-3">
                        <span
                            v-for="(color, index) in colors"
                            :key="index"
                            :title="color.label"
                            :class="{ '!border-black': color.id === currentVariant?.id }"
                            :style="{ backgroundColor: getVariantColor(color) ?? '#FFFFFF' }"
                            class="block h-8 w-8 rounded-full border-2 border-transparent hover:cursor-pointer"
                            @click="onVariantChange(color.id)"
                        ></span>
                    </div>
                </div>
                <div>
                    <template v-if="currentSubscriptionPlan?.summary">
                        <span class="mb-5 block text-left font-sans font-bold">{{ $t("What you get") }} :</span>
                        <Checklist :items="currentSubscriptionPlan.summary?.split(/\r?\n/)" />
                    </template>
                    <div class="text-center">
                        <Spoiler
                            v-if="subscriptionPlans?.length"
                            class="!m-0 inline-block w-full rounded-2xl !px-5 text-left lg:w-auto lg:min-w-[300px]"
                            title-class="rounded-2xl"
                            icon-class="w-3 h-3"
                        >
                            <template #title="{ opened }">
                                <div class="flex grow items-center font-poppins text-fxs">
                                    <SubscriptionIcon class="mr-5" />
                                    <span v-if="!currentSubscriptionPlan">
                                        {{ $t("Subscription plan") }}
                                    </span>
                                    <span
                                        v-else
                                        class="flex items-center text-base font-semibold before:mr-2 before:text-2xl before:text-red-500 before:content-['•']"
                                    >
                                        {{ currentSubscriptionPlan?.label }}
                                    </span>
                                </div>
                            </template>

                            <ul class="list-inside list-disc space-y-3 px-6 py-3 font-poppins text-sm">
                                <li v-for="subscriptionPlan in subscriptionPlans">
                                    <Button
                                        type="button"
                                        variant="link"
                                        class="!m-0"
                                        @click="onSubscriptionPlanChange(subscriptionPlan.id)"
                                    >
                                        {{ subscriptionPlan.label }}
                                    </Button>
                                </li>
                            </ul>
                        </Spoiler>
                    </div>
                    <div v-if="product.has_customization" class="bg-grey-light p-3 lg:px-20 lg:py-10">
                        <Label
                            for="customization_text"
                            class="!inline-block"
                            :value="$t('Please enter your box and kit number to add this item to your cart')"
                            required
                        />
                        <Textarea
                            id="customization_text"
                            rows="3"
                            v-model.trim="customizationText"
                            :has-error="'customization_text' in initialPageProps.errors"
                            :placeholder="$t('e.g., Box 23 Kit number 79')"
                            required
                        />
                        <p class="mt-2 text-sm text-gray-500 text-right">
                            {{ 250 - (customizationText?.length ?? 0) }} {{ $t("characters remaining") }}
                        </p>
                    </div>
                    <div
                        class="mb-10 mt-10 flex flex-col items-center justify-center text-center text-fsm font-medium max-lg:space-y-5 lg:flex-row lg:space-x-10"
                    >
                        <Button
                            v-if="currentVariant && allowSubscription"
                            :processing="!currentVariant.is_purchasable || cartStore.state.loading || (product.has_customization && !customizationText)"
                            bg-color="black"
                            text-color="white"
                            size="inline"
                            class="rounded-3xl py-4 text-fxs uppercase"
                            @click="handleAddToCart(true)"
                        >
                            {{ $t('Subscribe') }}
                        </Button>

                        <Button
                            v-if="currentVariant && allowPurchase"
                            :processing="!currentVariant.is_purchasable || cartStore.state.loading || (product.has_customization && !customizationText)"
                            :variant="allowSubscription ? 'bordered' : 'default'"
                            :bg-color="allowSubscription ? 'white' : 'black'"
                            :text-color="allowSubscription ? 'black' : 'white'"
                            size="inline"
                            class="rounded-3xl py-4 text-fxs uppercase"
                            @click="handleAddToCart(false)"
                        >
                            {{ currentVariant.is_preorder ? $t('Pre-order') : $t('Add to cart') }}
                        </Button>
                    </div>

                    <div
                        v-if="product.additional_information && width >= 1024"
                        class="prose mx-auto text-center text-fxs"
                        v-html="sanitize.html(product.additional_information)"
                    ></div>
                </div>
                <template v-if="width < 1024">
                    <div class="my-10 text-center lg:text-left">
                        <span class="block font-sans text-fsm font-bold"> {{ $t("Product overview") }} </span>
                        <div class="prose max-w-none text-fsm" v-html="sanitize.html(product.description)"></div>
                    </div>
                    <Spoiler
                        v-if="featuresToList !== ''"
                        :title="$t('Technical data')"
                        class="!px-5 !py-3 text-left"
                        icon-class="w-4 h-4"
                    >
                        <ul v-html="sanitize.html(featuresToList)"></ul>
                    </Spoiler>
                    <div
                        v-if="product.additional_information"
                        class="prose mx-auto mt-10 text-center text-fxs"
                        v-html="sanitize.html(product.additional_information)"
                    ></div>
                </template>
            </div>
        </div>

        <ProductSlider v-if="relatedProducts.length" :products="relatedProducts" />
    </Layout>
</template>
<style lang="postcss">
.single-product {
    @screen lg {
        @apply mb-20
        grid
        w-full
        grid-cols-2
        gap-14;
    }
}
.single-product__slider {
    @apply mb-14;
}
.single-product__details {
    @apply mb-10 text-center;

    @screen lg {
        @apply text-left;
    }
}
.single-product__overview .slider-ixo-navigation {
    @apply px-6;
}
</style>

